import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { TEACHER_STATUSES, type Page, type School, type TeacherRegister } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * Teacher register (packages/modules/m28-education/src/routes/teachers.ts).
 * Self-registration and adding qualification evidence are open to the
 * teacher themself; verifying evidence, moving PENDING_VERIFICATION ->
 * REGISTERED, and suspend/revoke are ministry-level actions the spec hands
 * to the same SCHOOL_ADMIN/ADMISSIONS_OFFICER staff roles that run school
 * administration — there is no dedicated "teacher registrar" role code in
 * the backend, so the portal's Teacher Registrar persona carries
 * ADMISSIONS_OFFICER (see server/personas.ts).
 */
export function Teachers() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles.includes('SCHOOL_ADMIN') || persona?.roles.includes('ADMISSIONS_OFFICER')) ?? false;
  const [status, setStatus] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (schoolId) qs.set('schoolId', schoolId);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<TeacherRegister>>(isStaff ? `/api/v1/m28/teachers?${qs.toString()}` : null);
  const schoolsQ = useApiQuery<Page<School>>('/api/v1/m28/schools?limit=100');

  return (
    <div>
      <PageHeader title="Teacher Register" subtitle="Registration, qualification verification, and register status." />
      {!isStaff ? (
        <div className="mb-4">
          <ErrorNote message="Listing the teacher register requires the School Administrator or Admissions Officer role." />
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <input className="field-input !w-52" placeholder="Search NIN / name…" value={q} onChange={(e) => setQ(e.target.value)} />
            <input className="field-input !w-40" placeholder="School id…" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} />
            <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {TEACHER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No teacher register entries match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">NIN</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">Specialisms</th>
                    <th className="table-head text-right">Qualifications</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((t) => (
                    <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(t.id)}>
                      <td className="table-cell font-mono text-xs">{t.nin}</td>
                      <td className="table-cell font-medium">{t.fullName}</td>
                      <td className="table-cell text-xs text-slate-500">{t.subjectSpecialisms.join(', ') || '—'}</td>
                      <td className="table-cell text-right tabular-nums">
                        {t.qualifications.filter((q) => q.verified).length}/{t.qualifications.length} verified
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={t.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {detailId ? (
        <TeacherDetailModal
          id={detailId}
          isStaff={isStaff}
          schools={schoolsQ.data?.items ?? []}
          onClose={() => setDetailId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function TeacherDetailModal({
  id,
  isStaff,
  schools,
  onClose,
  onChanged,
}: {
  id: string;
  isStaff: boolean;
  schools: School[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<TeacherRegister>(`/api/v1/m28/teachers/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState<'suspend' | 'revoke' | null>(null);
  const [currentSchoolId, setCurrentSchoolId] = useState('');
  const [credential, setCredential] = useState('');
  const [institution, setInstitution] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [showAddQual, setShowAddQual] = useState(false);

  const teacher = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/teachers/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowReason(null);
      setShowAddQual(false);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Teacher register entry" onClose={onClose} wide>
      {detailQ.loading || !teacher ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={teacher.status} />
            <span className="font-semibold">{teacher.fullName}</span>
            <span className="font-mono text-xs text-slate-400">{teacher.nin}</span>
          </div>
          <p className="text-xs text-slate-500">Specialisms: {teacher.subjectSpecialisms.join(', ') || '—'}</p>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qualifications</div>
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAddQual((s) => !s)}>
                + Add qualification
              </button>
            </div>
            {showAddQual ? (
              <div className="mb-3 grid grid-cols-4 items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <Field label="Credential" className="!mb-0">
                  <input className="field-input !py-1 text-xs" value={credential} onChange={(e) => setCredential(e.target.value)} />
                </Field>
                <Field label="Institution" className="!mb-0">
                  <input className="field-input !py-1 text-xs" value={institution} onChange={(e) => setInstitution(e.target.value)} />
                </Field>
                <Field label="Year" className="!mb-0">
                  <input className="field-input !py-1 text-xs" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
                </Field>
                <button
                  className="btn-primary !py-1.5 text-xs"
                  disabled={busy || credential.trim().length < 2 || institution.trim().length < 2}
                  onClick={() => act('/qualifications', { credential, institution, year: Number.parseInt(year, 10) })}
                >
                  Add
                </button>
              </div>
            ) : null}
            <ul className="space-y-1.5">
              {teacher.qualifications.map((q) => (
                <li key={q.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                  <span>
                    {q.credential} — {q.institution} ({q.year})
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={q.verified ? 'VALID' : 'PENDING'} />
                    {isStaff && !q.verified ? (
                      <button className="btn-secondary !px-2 !py-0.5 text-[11px]" disabled={busy} onClick={() => act(`/qualifications/${q.id}/verify`)}>
                        Verify
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {isStaff ? (
            <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
              {teacher.status === 'PENDING_VERIFICATION' ? (
                <>
                  <Field label="Assign to school (optional)" className="!mb-0">
                    <select className="field-input !w-56" value={currentSchoolId} onChange={(e) => setCurrentSchoolId(e.target.value)}>
                      <option value="">— None —</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <button
                    className="btn-primary !px-2.5 !py-1 text-xs"
                    disabled={busy || !teacher.qualifications.some((q) => q.verified)}
                    onClick={() => act('/register', currentSchoolId ? { currentSchoolId } : undefined)}
                  >
                    Register (requires ≥1 verified qualification)
                  </button>
                </>
              ) : null}
              {teacher.status === 'REGISTERED' ? (
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReason('suspend')}>
                  Suspend
                </button>
              ) : null}
              {['REGISTERED', 'SUSPENDED'].includes(teacher.status) ? (
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReason('revoke')}>
                  Revoke
                </button>
              ) : null}
            </div>
          ) : null}

          {showReason ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => act(`/${showReason}`, { reason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowReason(null)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
